import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, X } from "lucide-react";
import useProfanityFilter from "../../../hooks/useProfanityFilter";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../ui/button";

const ChatInput = ({ onSendMessage }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const { checkProfanity } = useProfanityFilter();

  const handleSend = () => {
    if (!message.trim()) return; // Prevent sending empty messages

    const cleanedMessage = checkProfanity(message.trim());

    if (cleanedMessage.includes("*")) {
      setPopupMessage("Your message contains inappropriate language. Please revise it.");
      return; // Prevent sending the message
    }

    onSendMessage(cleanedMessage);
    setMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative">
      {/* Input area */}
      <div className="flex items-end gap-1.5 sm:gap-2">
        <textarea
          className="
            flex-1
            min-h-[40px] sm:min-h-[44px] max-h-32
            px-3 sm:px-4 py-2.5 sm:py-3
            bg-white
            border
            rounded-2xl
            text-xs sm:text-sm text-foreground
            placeholder:text-muted-foreground
            resize-none
            focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
            transition-all duration-200
          "
          style={{ borderColor: 'rgb(194, 208, 205)' }}
          placeholder={t('type_message_placeholder', 'Type a message...')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          rows={1}
        />
        <Button
          onClick={handleSend}
          disabled={!message.trim()}
          size="icon"
          className="
            h-10 w-10 sm:h-11 sm:w-11
            rounded-full
            bg-primary hover:bg-primary/90
            text-primary-foreground
            flex items-center justify-center
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-200
          "
        >
          <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </Button>
      </div>

      {/* Animated Popup for Profanity Warning */}
      <AnimatePresence>
        {popupMessage && (
          <motion.div
            className="
              absolute bottom-full left-0 right-0 mb-2
              bg-destructive text-destructive-foreground
              px-4 py-3
              rounded-xl
              shadow-lg
              flex items-center justify-between gap-3
            "
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-sm">{popupMessage}</span>
            <button 
              className="
                w-6 h-6 
                rounded-full 
                bg-white/20 
                hover:bg-white/30
                flex items-center justify-center
                transition-colors
              "
              onClick={() => setPopupMessage("")}
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatInput;
